import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatHours } from './constants';
import { useTranslation } from 'react-i18next';

ChartJS.register(ArcElement, Tooltip, Legend);

const DECLARED_COLOR = '#ffffff';
const REMAINING_COLOR = '#8db4ff';

export default function ResourceDriftCard({ resourceDrift, theoretical }) {
  const { t } = useTranslation('planning');
  const declared = Number(resourceDrift?.declared || 0);
  const remaining = Number(resourceDrift?.remaining || 0);
  const totalTheoretical = Number(theoretical || declared + remaining);
  const hasData = totalTheoretical > 0;
  const remainingPct = hasData ? (remaining / totalTheoretical) * 100 : 0;
  const declaredPct = hasData ? (declared / totalTheoretical) * 100 : 0;

  const data = useMemo(
    () => ({
      labels: [t('declaredWorkload'), t('remainingCapacity')],
      datasets: [
        {
          data: hasData ? [declared, remaining] : [1],
          backgroundColor: hasData ? [DECLARED_COLOR, REMAINING_COLOR] : ['rgba(255,255,255,0.18)'],
          borderWidth: 0,
          hoverOffset: hasData ? 6 : 0,
        },
      ],
    }),
    [declared, remaining, hasData, t]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '72%',
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: hasData,
          backgroundColor: '#0f172a',
          titleColor: '#ffffff',
          bodyColor: '#e2e8f0',
          padding: 10,
          cornerRadius: 8,
          callbacks: {
            label: (ctx) => ` ${ctx.label}: ${formatHours(ctx.parsed)} ${t('hrsSuffix')}`,
          },
        },
      },
    }),
    [hasData, t]
  );

  const metrics = [
    {
      key: 'declared',
      label: t('declared'),
      value: `${formatHours(declared)}h`,
      percent: declaredPct,
      swatch: DECLARED_COLOR,
      hint: t('cappedAtHrsDay'),
    },
    {
      key: 'remaining',
      label: t('remaining'),
      value: `${formatHours(remaining)}h`,
      percent: remainingPct,
      swatch: REMAINING_COLOR,
      hint: t('unusedCapacity'),
    },
  ];

  return (
    <div className="resource-drift-card">
      <div className="resource-drift-glow resource-drift-glow--tl" aria-hidden="true" />
      <div className="resource-drift-glow resource-drift-glow--br" aria-hidden="true" />

      <div className="resource-drift-inner">
        <div className="resource-drift-header">
          <div className="resource-drift-icon" aria-hidden="true">
            <span className="material-symbols-outlined">donut_large</span>
          </div>
          <div className="min-w-0">
            <h3 className="resource-drift-title">{t('resourceDriftTitle')}</h3>
            <p className="resource-drift-subtitle">
              {t('resourceDriftSubtitle')}
            </p>
          </div>
        </div>

        <div className="resource-drift-body">
          <div className="resource-drift-chart-wrap">
            <div className="resource-drift-chart-ring">
              <div className="resource-drift-chart">
                <Doughnut data={data} options={options} />
                <div className="resource-drift-center">
                  {hasData ? (
                    <>
                      <span className="resource-drift-center-label">{t('remaining')}</span>
                      <span className="resource-drift-center-value">{formatHours(remaining)}h</span>
                      <span className="resource-drift-center-meta">
                        {remainingPct.toFixed(0)}% of {formatHours(totalTheoretical)}h
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="resource-drift-center-label">{t('capacity')}</span>
                      <span className="resource-drift-center-value">0h</span>
                      <span className="resource-drift-center-meta">{t('noRangeSelected')}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="resource-drift-metrics">
            {metrics.map((item) => (
              <div key={item.key} className="resource-drift-metric">
                <div className="resource-drift-metric-top">
                  <span className="resource-drift-metric-label">
                    <span
                      className="resource-drift-swatch"
                      style={{ background: item.swatch }}
                      aria-hidden="true"
                    />
                    {item.label}
                  </span>
                  <span className="resource-drift-metric-value">{item.value}</span>
                </div>
                <div className="resource-drift-meter" aria-hidden="true">
                  <div
                    className="resource-drift-meter-fill"
                    style={{
                      width: `${Math.max(0, Math.min(100, item.percent))}%`,
                      background: item.swatch,
                    }}
                  />
                </div>
                <div className="resource-drift-metric-bottom">
                  <span>{item.hint}</span>
                  <span>{item.percent.toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
